import React, { useEffect, useState } from "react";
import { http } from "@/context/AuthContext";
import { Loader2, Plus, X } from "lucide-react";
import { SERVICES } from "@/lib/data";

function NewAppointmentDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({
    user_email: "",
    title: "",
    starts_at: "",
    duration_minutes: 30,
    service_slug: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      await http.post("/admin/appointments", {
        ...form,
        service_slug: form.service_slug || null,
        starts_at: new Date(form.starts_at).toISOString(),
        duration_minutes: parseInt(form.duration_minutes),
      });
      onSaved?.();
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to create appointment");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-navy/40 z-50 flex items-center justify-center p-4" data-testid="new-appt-dialog">
      <form onSubmit={submit} className="bg-white rounded-3xl p-6 max-w-md w-full space-y-3 shadow-cardHover">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl text-navy">New Appointment</h3>
          <button type="button" onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-4 h-4" /></button>
        </div>
        <input required type="email" data-testid="appt-email" placeholder="Client email" value={form.user_email} onChange={(e) => setForm({ ...form, user_email: e.target.value })} className="w-full border border-navy/15 rounded-xl px-4 py-2.5 text-sm" />
        <input required data-testid="appt-title" placeholder="Title (e.g. Credit Analysis Review)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-navy/15 rounded-xl px-4 py-2.5 text-sm" />
        <input required type="datetime-local" data-testid="appt-starts" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="w-full border border-navy/15 rounded-xl px-4 py-2.5 text-sm" />
        <div className="grid grid-cols-2 gap-3">
          <select data-testid="appt-duration" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="border border-navy/15 rounded-xl px-4 py-2.5 text-sm bg-white">
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
            <option value={45}>45 min</option>
            <option value={60}>60 min</option>
            <option value={90}>90 min</option>
          </select>
          <select data-testid="appt-service" value={form.service_slug} onChange={(e) => setForm({ ...form, service_slug: e.target.value })} className="border border-navy/15 rounded-xl px-4 py-2.5 text-sm bg-white">
            <option value="">Service (optional)</option>
            {SERVICES.map((s) => (<option key={s.slug} value={s.slug}>{s.name}</option>))}
          </select>
        </div>
        <textarea rows={3} placeholder="Notes (optional)" data-testid="appt-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-navy/15 rounded-xl px-4 py-2.5 text-sm" />
        {err && <div className="text-red-500 text-xs">{err}</div>}
        <button type="submit" disabled={saving} data-testid="appt-submit" className="btn-primary w-full disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Appointment
        </button>
      </form>
    </div>
  );
}

export default function AdminAppointments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);

  function refresh() {
    setLoading(true);
    http.get("/admin/appointments").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  return (
    <div data-testid="admin-appointments">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl">Appointments</h1>
          <p className="mt-2 text-navy/65">All client appointments across services. Acuity-synced + manually added.</p>
        </div>
        <button onClick={() => setShowDialog(true)} data-testid="new-appt-btn" className="btn-primary">
          <Plus className="w-4 h-4" /> Manual Appointment
        </button>
      </div>
      <div className="mt-8">
        {loading ? <div className="flex items-center gap-2 text-navy/60"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div> :
        items.length === 0 ? <div className="card p-10 text-center text-navy/65">No appointments yet.</div> :
        <ul className="card divide-y divide-navy/5">
          {items.map((a, i) => (
            <li key={a.appointment_id || i} className="p-4 flex items-center justify-between text-sm" data-testid={`admin-appt-${i}`}>
              <div>
                <div className="text-navy font-medium">{a.title}</div>
                <div className="text-xs text-navy/55">{a.starts_at}{a.duration_minutes && ` · ${a.duration_minutes} min`} · {a.user_email || `user ${a.user_id}`}{a.source === "manual" && " · manual"}</div>
              </div>
              <span className="text-xs uppercase tracking-widest text-gold">{a.status || a.service_slug || "scheduled"}</span>
            </li>
          ))}
        </ul>}
      </div>
      {showDialog && <NewAppointmentDialog onClose={() => setShowDialog(false)} onSaved={refresh} />}
    </div>
  );
}
